export function foldSearchText(value: string): string {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function escapeRegExp(value: string): string {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Match whole tokens / delimited segments - avoids partial token false positives. */
export function fieldMatchesWord(fieldF: string, word: string): boolean {
  if (!fieldF || !word) return false;
  const tokens = fieldF.split(/[^a-z0-9\u0900-\u097f]+/).filter(Boolean);
  if (tokens.includes(word)) return true;
  const re = new RegExp(
    `(?:^|[^a-z0-9\u0900-\u097f])${escapeRegExp(word)}(?:[^a-z0-9\u0900-\u097f]|$)`,
  );
  return re.test(fieldF);
}

export function findMatchSnippet(terms: string, queryWords: string[]): string {
  const termsList = terms.split(/\s+/);
  for (const qw of queryWords) {
    const fq = foldSearchText(qw);
    if (fq.length < 2) continue;
    for (let i = 0; i < termsList.length; i += 1) {
      if (foldSearchText(termsList[i]).includes(fq)) {
        const start = Math.max(0, i - 1);
        const end = Math.min(termsList.length, i + 3);
        return termsList.slice(start, end).join(' ');
      }
    }
  }
  return '';
}

export type ScoredSearchFields = {
  labelEn: string;
  labelHi?: string;
  groupEn: string;
  groupHi?: string;
  terms: string;
};

export function scoreSearchMatch(
  fields: ScoredSearchFields,
  query: string,
  queryWords: string[],
): number {
  const labelEnF = foldSearchText(fields.labelEn);
  const labelHiF = foldSearchText(fields.labelHi ?? '');
  const groupEnF = foldSearchText(fields.groupEn);
  const groupHiF = foldSearchText(fields.groupHi ?? '');
  const termsF = foldSearchText(fields.terms);
  const hay = `${groupEnF} ${groupHiF} ${labelEnF} ${labelHiF} ${termsF}`;

  let rank = 0;

  if (labelEnF.includes(query) || labelHiF.includes(query)) rank += 300;
  else if (hay.includes(query)) rank += 200;

  let matchedWordCount = 0;
  for (const w of queryWords) {
    let wordMatched = false;
    if (fieldMatchesWord(labelEnF, w) || fieldMatchesWord(labelHiF, w)) {
      rank += 60;
      wordMatched = true;
    } else if (fieldMatchesWord(termsF, w)) {
      rank += 40;
      wordMatched = true;
    } else if (fieldMatchesWord(groupEnF, w) || fieldMatchesWord(groupHiF, w)) {
      rank += 20;
      wordMatched = true;
    }
    if (wordMatched) matchedWordCount += 1;
  }

  if (queryWords.length > 1 && matchedWordCount > 0 && matchedWordCount < queryWords.length) {
    rank = Math.floor(rank * (matchedWordCount / queryWords.length));
  }
  if (queryWords.length > 1 && matchedWordCount === 0) rank = 0;

  if (labelEnF.startsWith(query) || labelHiF.startsWith(query)) rank += 100;
  else if (groupEnF.startsWith(query) || groupHiF.startsWith(query)) rank += 50;

  return rank;
}
